import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import PostsInfiniteList from "@sprout/client-components/dist/PostsInfiniteList";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import update from "immutability-helper";
import { useCallback, useMemo } from "react";
import { TeamMemberLevel, useProfilePagePostsTeamQuery, useProfilePagePostsUserQuery, useProfilePageUser_CurrentUserProfilePostsQuery, UserType } from "sprout-graphql";
import { DividerText as BaseDividerText } from "sprout-ui-dom";
import { useSnackbar } from "sprout-ui-dom/dist/Snackbar";
import { Box, Text } from "sprout-ui-primitives";
import { media, themeGet } from "sprout-ui-theme";
import styled from "styled-components";
const ProfilePageUserPostsWrapper = styled(Box)`
  ${media.up("medium", "pointer")} {
    background: ${themeGet("colors.white.100")};
    box-shadow: 0 0 0 ${themeGet("colors.black.10")};
    border-radius: 30px;
    padding: 48px;
  }
`;
const DividerText = styled(BaseDividerText)`
  margin-bottom: 32px;

  display: none;

  .ProfilePageUserTopics + & {
    display: flex;
  }
`;
const ProfilePageUserPostsEmptyText = styled(Text)`
  .ProfilePageUserTopics + & {
    display: none;
  }
`;
const ProfilePageUserPosts = (props)=>{
    var ref9, ref1, ref2, ref3, ref4, ref5;
    const { user  } = props;
    const { id , memberLevel , type , username  } = user;
    const currentUser = useCurrentUser();
    const isCurrentUser = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) === id;
    const isOwnerOrAdmin = memberLevel === TeamMemberLevel.Owner || memberLevel === TeamMemberLevel.Admin;
    const useQuery = type === UserType.Team ? useProfilePagePostsTeamQuery : useProfilePagePostsUserQuery;
    const { loading , data , error , fetchMore  } = useQuery({
        variables: {
            username
        },
        skip: isOwnerOrAdmin || isCurrentUser,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first"
    });
    const { loading: currentUserProfileLoading , data: currentUserProfileData , error: currentUserProfileError , fetchMore: currentUserProfileFetchMore ,  } = useProfilePageUser_CurrentUserProfilePostsQuery({
        skip: !isCurrentUser,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first"
    });
    const posts = (data === null || data === void 0 ? void 0 : (ref9 = data.user) === null || ref9 === void 0 ? void 0 : ref9.posts.nodes) || (currentUserProfileData === null || currentUserProfileData === void 0 ? void 0 : (ref1 = currentUserProfileData.currentUser) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.specialTopic) === null || ref2 === void 0 ? void 0 : ref2.posts.nodes);
    const pageInfo = (data === null || data === void 0 ? void 0 : (ref3 = data.user) === null || ref3 === void 0 ? void 0 : ref3.posts.pageInfo) || (currentUserProfileData === null || currentUserProfileData === void 0 ? void 0 : (ref4 = currentUserProfileData.currentUser) === null || ref4 === void 0 ? void 0 : (ref5 = ref4.specialTopic) === null || ref5 === void 0 ? void 0 : ref5.posts.pageInfo);
    const { endCursor =null , hasNextPage =false  } = pageInfo || {};
    const showSnackbar = useSnackbar();
    const loadMore = useCallback(()=>{
        if (isCurrentUser) currentUserProfileFetchMore({
            variables: {
                endCursor
            },
            updateQuery (prevResult, { fetchMoreResult  }) {
                var ref, ref6, ref7, ref8;
                const fetchMoreResultsNodes = fetchMoreResult === null || fetchMoreResult === void 0 ? void 0 : (ref = fetchMoreResult.currentUser) === null || ref === void 0 ? void 0 : (ref6 = ref.specialTopic) === null || ref6 === void 0 ? void 0 : ref6.posts.nodes;
                const fetchMoreResultsPageInfo = fetchMoreResult === null || fetchMoreResult === void 0 ? void 0 : (ref7 = fetchMoreResult.currentUser) === null || ref7 === void 0 ? void 0 : (ref8 = ref7.specialTopic) === null || ref8 === void 0 ? void 0 : ref8.posts.pageInfo;
                if (fetchMoreResultsNodes && fetchMoreResultsPageInfo) return update(prevResult, {
                    currentUser: {
                        specialTopic: {
                            posts: {
                                nodes: {
                                    $push: fetchMoreResultsNodes
                                },
                                pageInfo: {
                                    $set: fetchMoreResultsPageInfo
                                }
                            }
                        }
                    }
                });
                return prevResult;
            }
        });
        else fetchMore({
            variables: {
                endCursor
            },
            updateQuery (prevResult, { fetchMoreResult  }) {
                var ref, ref10;
                const fetchMoreResultsNodes = fetchMoreResult === null || fetchMoreResult === void 0 ? void 0 : (ref = fetchMoreResult.user) === null || ref === void 0 ? void 0 : ref.posts.nodes;
                const fetchMoreResultsPageInfo = fetchMoreResult === null || fetchMoreResult === void 0 ? void 0 : (ref10 = fetchMoreResult.user) === null || ref10 === void 0 ? void 0 : ref10.posts.pageInfo;
                if (fetchMoreResultsNodes && fetchMoreResultsPageInfo) return update(prevResult, {
                    user: {
                        posts: {
                            nodes: {
                                $push: fetchMoreResultsNodes
                            },
                            pageInfo: {
                                $set: fetchMoreResultsPageInfo
                            }
                        }
                    }
                });
                return prevResult;
            }
        }).catch(()=>{
            showSnackbar.error();
        });
    }, [
        currentUserProfileFetchMore,
        endCursor,
        fetchMore,
        isCurrentUser,
        showSnackbar, 
    ]);
    const postProps = useMemo(()=>({
            showUser: type === UserType.Team,
            showTeam: type === UserType.User,
            showPseudoBreadcrumbs: !isCurrentUser,
            showCompleted: false
        }), [
        isCurrentUser,
        type
    ]);
    if (isOwnerOrAdmin) return null;
    if ((loading || currentUserProfileLoading) && !posts) return null;
    if (!posts || error || currentUserProfileError) return null;
    if (posts.length === 0) return /*#__PURE__*/ _jsx(ProfilePageUserPostsEmptyText, {
        textAlign: "center",
        mt: 7,
        mb: 11,
        children: isCurrentUser ? "Nothing shared publicly yet" : `${username} hasn't shared anything yet`
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(DividerText, {
                text: isCurrentUser ? "Uncategorized" : "Leaves"
            }),
            /*#__PURE__*/ _jsx(ProfilePageUserPostsWrapper, {
                children: /*#__PURE__*/ _jsx(PostsInfiniteList, {
                    multiselect: false,
                    hasNextPage: hasNextPage,
                    loadMore: loadMore,
                    loading: loading,
                    posts: posts,
                    postProps: postProps
                })
            })
        ]
    });
};
export default ProfilePageUserPosts;
